import XLSX from "xlsx";

/**
 * Bulk-import van verkoopprijzen: parsen van het Excel-bestand en het opbouwen van het
 * controleoverzicht (welk product krijgt welke prijs).
 *
 * Dit bestand bevat opzettelijk GEEN Firestore- of React-afhankelijkheden: alles hier is pure
 * logica die los te testen is (zie priceImport.test.js). De schrijfkant staat in
 * functions/firestore/priceImport.js.
 */

/** Status van een regel in het controleoverzicht. */
export const PRICE_IMPORT_STATUS = {
  CHANGE: "change",
  UNCHANGED: "unchanged",
  ERROR: "error",
};

/** Blokkerende problemen: deze regels worden nooit doorgevoerd. */
export const PRICE_IMPORT_ERROR = {
  MISSING_CODE: "missing_code",
  MISSING_PRICE: "missing_price",
  INVALID_PRICE: "invalid_price",
  PRICE_NOT_POSITIVE: "price_not_positive",
  PRICE_TOO_HIGH: "price_too_high",
  PRODUCT_NOT_FOUND: "product_not_found",
  DUPLICATE_CODE_IN_FILE: "duplicate_code_in_file",
  AMBIGUOUS_CODE: "ambiguous_code",
};

/** Aandachtspunten: de regel mag door, maar de gebruiker moet er bewust naar kijken. */
export const PRICE_IMPORT_WARNING = {
  LARGE_INCREASE: "large_increase",
  LARGE_DECREASE: "large_decrease",
  NAME_MISMATCH: "name_mismatch",
  GROUP_PRICES: "group_prices",
  HIDDEN_PRODUCT: "hidden_product",
  NO_CURRENT_PRICE: "no_current_price",
  ROUNDED: "rounded",
  AMBIGUOUS_SEPARATOR: "ambiguous_separator",
};

/** Fouten op bestandsniveau (er valt dan niets te previewen). */
export const PRICE_IMPORT_FILE_ERROR = {
  EMPTY_FILE: "empty_file",
  HEADER_NOT_FOUND: "header_not_found",
  NO_ROWS: "no_rows",
  UNREADABLE: "unreadable",
};

/** Vanaf welke procentuele afwijking we een wijziging als "opvallend" markeren. */
export const LARGE_CHANGE_PERCENTAGE = 25;

/** Bovengrens als vangnet tegen bv. een verkeerd geplaatste duizendscheiding. */
export const MAX_PRICE = 100000;

/** Prijzen worden op centen vergeleken; alles daaronder is float-ruis. */
export const PRICE_TOLERANCE = 0.005;

const NAME_HEADERS = ["productnaam", "naam", "product", "productname", "name", "omschrijving", "artikel"];
const CODE_HEADERS = ["code", "productcode", "artikelcode", "artikelnummer", "sku", "itemcode"];
const PRICE_HEADERS = [
  "prijs",
  "verkoopprijs",
  "nieuweprijs",
  "nieuweverkoopprijs",
  "price",
  "salesprice",
  "adviesprijs",
];

const normalizeHeader = (value) =>
  String(value ?? "")
    .toLowerCase()
    .replace(/[\s._\-/]/g, "")
    .trim();

/**
 * Normaliseert een productcode voor het matchen. Zowel de code uit het bestand als de code uit
 * het CMS gaan hier door, zodat spaties/hoofdletterverschillen nooit een mismatch veroorzaken.
 */
export const normalizeCode = (code) =>
  String(code ?? "")
    .replace(/\s/g, "")
    .trim()
    .toUpperCase();

/** Losse vergelijking van productnamen, alleen om een naamverschil te kunnen signaleren. */
export const normalizeName = (name) =>
  String(name ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

export const roundPrice = (value) => Math.round((Number(value) + Number.EPSILON) * 100) / 100;

export const pricesAreEqual = (a, b) => Math.abs(toNumber(a) - toNumber(b)) < PRICE_TOLERANCE;

/** Zet een waarde om naar een getal en geeft 0 terug voor niet-getallen (bv. null of ""). */
export const toNumber = (value) => {
  const parsed = typeof value === "number" ? value : parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Parseert een prijs uit een cel. Accepteert zowel getallen (numerieke cel) als tekst met
 * valutateken en Nederlandse of Engelse decimaalscheiding.
 *
 * @returns {{value: number|null, error: string|null, warnings: string[]}}
 */
export const parsePriceValue = (raw) => {
  const warnings = [];

  if (raw === null || raw === undefined || (typeof raw === "string" && raw.trim() === "")) {
    return { value: null, error: PRICE_IMPORT_ERROR.MISSING_PRICE, warnings };
  }

  let value;

  if (typeof raw === "number") {
    if (!Number.isFinite(raw)) return { value: null, error: PRICE_IMPORT_ERROR.INVALID_PRICE, warnings };
    value = raw;
  } else {
    // Valutatekens, (non-breaking) spaties en Zwitserse duizendscheiding weghalen.
    let cleaned = String(raw)
      .replace(/[\u00A0\u202F]/g, "")
      .replace(/€|eur/gi, "")
      .replace(/['’`]/g, "")
      .replace(/\s/g, "")
      .trim();

    if (cleaned === "") return { value: null, error: PRICE_IMPORT_ERROR.MISSING_PRICE, warnings };
    if (!/^-?[\d.,]+$/.test(cleaned)) return { value: null, error: PRICE_IMPORT_ERROR.INVALID_PRICE, warnings };

    const lastComma = cleaned.lastIndexOf(",");
    const lastDot = cleaned.lastIndexOf(".");

    if (lastComma !== -1 && lastDot !== -1) {
      // Beide scheidingstekens aanwezig: de laatste is de decimaalscheiding.
      if (lastComma > lastDot) cleaned = cleaned.replace(/\./g, "").replace(",", ".");
      else cleaned = cleaned.replace(/,/g, "");
    } else if (lastComma !== -1 || lastDot !== -1) {
      const separator = lastComma !== -1 ? "," : ".";
      const lastIndex = lastComma !== -1 ? lastComma : lastDot;
      const occurrences = cleaned.split(separator).length - 1;
      const decimals = cleaned.length - lastIndex - 1;

      if (occurrences > 1) {
        // Bv. "1.234.567": alleen als duizendscheiding te lezen.
        cleaned = cleaned.split(separator).join("");
      } else if (decimals === 3) {
        // "1.500" / "1,500" is dubbelzinnig. We lezen het als duizendscheiding en markeren de
        // regel, zodat de gebruiker het in het controlescherm ziet staan.
        warnings.push(PRICE_IMPORT_WARNING.AMBIGUOUS_SEPARATOR);
        cleaned = cleaned.replace(separator, "");
      } else {
        cleaned = cleaned.replace(separator, ".");
      }
    }

    value = parseFloat(cleaned);
    if (!Number.isFinite(value)) return { value: null, error: PRICE_IMPORT_ERROR.INVALID_PRICE, warnings };
  }

  const rounded = roundPrice(value);
  // Bewust een veel kleinere marge dan PRICE_TOLERANCE: hier gaat het erom of er daadwerkelijk
  // iets is afgerond, niet of twee prijzen op centniveau gelijk zijn.
  if (Math.abs(rounded - value) > 1e-9) warnings.push(PRICE_IMPORT_WARNING.ROUNDED);

  if (rounded <= 0) return { value: rounded, error: PRICE_IMPORT_ERROR.PRICE_NOT_POSITIVE, warnings };
  if (rounded > MAX_PRICE) return { value: rounded, error: PRICE_IMPORT_ERROR.PRICE_TOO_HIGH, warnings };

  return { value: rounded, error: null, warnings };
};

/**
 * Zoekt de kolomindeling in een sheet. De koprij hoeft niet de eerste rij te zijn en extra
 * kolommen (bv. de rest van de productexport) worden simpelweg genegeerd.
 *
 * @returns {{headerRowIndex: number, columns: {name: number|null, code: number, price: number}}|null}
 */
export const findHeaderRow = (rows) => {
  const maxScan = Math.min(rows.length, 15);

  for (let index = 0; index < maxScan; index++) {
    const row = rows[index] || [];
    const headers = row.map(normalizeHeader);

    const codeIndex = headers.findIndex((header) => CODE_HEADERS.includes(header));
    const priceIndex = headers.findIndex((header) => PRICE_HEADERS.includes(header));
    const nameIndex = headers.findIndex((header) => NAME_HEADERS.includes(header));

    if (codeIndex !== -1 && priceIndex !== -1) {
      return {
        headerRowIndex: index,
        columns: { name: nameIndex === -1 ? null : nameIndex, code: codeIndex, price: priceIndex },
      };
    }
  }

  return null;
};

/**
 * Leest de ruwe regels uit een werkboek. Pakt de eerste sheet met een geldige koprij.
 *
 * @returns {{rows: Array, sheetName: string, columns: Object, error: null}|{error: string}}
 */
export const parseWorkbook = (workbook) => {
  const sheetNames = workbook?.SheetNames || [];
  if (sheetNames.length === 0) return { error: PRICE_IMPORT_FILE_ERROR.EMPTY_FILE };

  for (const sheetName of sheetNames) {
    const sheet = workbook.Sheets[sheetName];
    if (!sheet) continue;

    // blankrows: true is belangrijk - daarmee blijft de index in sheetRows gelijk aan de rij in
    // Excel, zodat het regelnummer in het controlescherm klopt ook als er lege rijen tussen staan.
    const sheetRows = XLSX.utils.sheet_to_json(sheet, { header: 1, blankrows: true, defval: null, raw: true });
    const header = findHeaderRow(sheetRows);
    if (!header) continue;

    // Een werkblad hoeft niet op rij 1 te beginnen; die verschuiving tellen we mee.
    const rangeOffset = sheet["!ref"] ? XLSX.utils.decode_range(sheet["!ref"]).s.r : 0;

    const rows = [];
    for (let index = header.headerRowIndex + 1; index < sheetRows.length; index++) {
      const row = sheetRows[index] || [];
      const code = row[header.columns.code];
      const price = row[header.columns.price];
      const name = header.columns.name === null ? null : row[header.columns.name];

      const isEmpty = [code, price, name].every(
        (value) => value === null || value === undefined || String(value).trim() === ""
      );
      if (isEmpty) continue;

      rows.push({
        rowNumber: rangeOffset + index + 1, // 1-based, zoals Excel het toont
        name: name === null || name === undefined ? "" : String(name).trim(),
        code: code === null || code === undefined ? "" : String(code).trim(),
        rawPrice: price,
      });
    }

    if (rows.length === 0) return { error: PRICE_IMPORT_FILE_ERROR.NO_ROWS };

    return { rows, sheetName, columns: header.columns, error: null };
  }

  return { error: PRICE_IMPORT_FILE_ERROR.HEADER_NOT_FOUND };
};

/** Leest een geüpload bestand (File/Blob) in en parseert het. */
export const parsePriceImportFile = async (file) => {
  try {
    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(new Uint8Array(buffer), { type: "array" });
    return parseWorkbook(workbook);
  } catch (error) {
    console.error("Kon prijsbestand niet lezen:", error);
    return { error: PRICE_IMPORT_FILE_ERROR.UNREADABLE };
  }
};

/**
 * Bouwt het controleoverzicht: per bestandsregel het gematchte product, de huidige prijs, de
 * nieuwe prijs en eventuele fouten/aandachtspunten.
 *
 * @param {Array} fileRows Uitkomst van parsePriceImportFile
 * @param {Array} products Producten uit het CMS
 * @returns {Array} regels in bestandsvolgorde
 */
export const buildPriceImportPlan = (fileRows, products) => {
  const productsByCode = new Map();
  (products || []).forEach((product) => {
    const code = normalizeCode(product?.code);
    if (!code) return;
    if (!productsByCode.has(code)) productsByCode.set(code, []);
    productsByCode.get(code).push(product);
  });

  // Dubbele codes binnen het bestand: alle betrokken regels worden geblokkeerd, want we kunnen
  // niet weten welke regel de bedoelde prijs bevat.
  const codeCounts = new Map();
  (fileRows || []).forEach((row) => {
    const code = normalizeCode(row.code);
    if (!code) return;
    codeCounts.set(code, (codeCounts.get(code) || 0) + 1);
  });

  return (fileRows || []).map((row, index) => {
    const code = normalizeCode(row.code);
    const parsedPrice = parsePriceValue(row.rawPrice);
    const warnings = [...parsedPrice.warnings];

    const line = {
      key: `${row.rowNumber}-${index}`,
      rowNumber: row.rowNumber,
      fileName: row.name || "",
      fileCode: row.code || "",
      normalizedCode: code,
      rawPrice: row.rawPrice,
      newPrice: parsedPrice.value,
      productId: null,
      productName: null,
      productCode: null,
      oldPrice: null,
      discount: null,
      delta: null,
      deltaPercentage: null,
      status: PRICE_IMPORT_STATUS.ERROR,
      error: null,
      warnings,
      matches: [],
    };

    if (!code) {
      line.error = PRICE_IMPORT_ERROR.MISSING_CODE;
      return line;
    }

    if (codeCounts.get(code) > 1) {
      line.error = PRICE_IMPORT_ERROR.DUPLICATE_CODE_IN_FILE;
      return line;
    }

    const matches = productsByCode.get(code) || [];
    if (matches.length === 0) {
      line.error = PRICE_IMPORT_ERROR.PRODUCT_NOT_FOUND;
      return line;
    }
    if (matches.length > 1) {
      line.error = PRICE_IMPORT_ERROR.AMBIGUOUS_CODE;
      line.matches = matches.map((product) => ({ id: product.id, name: product.name }));
      return line;
    }

    const product = matches[0];
    const oldPrice = toNumber(product.price);

    line.productId = product.id;
    line.productName = product.name || "";
    line.productCode = product.code || "";
    line.oldPrice = oldPrice;
    line.discount = toNumber(product.discount);

    if (parsedPrice.error) {
      line.error = parsedPrice.error;
      return line;
    }

    if (!Number.isFinite(Number(product.price)) || Number(product.price) <= 0) {
      warnings.push(PRICE_IMPORT_WARNING.NO_CURRENT_PRICE);
    }
    if (product.hideVariant) {
      warnings.push(PRICE_IMPORT_WARNING.HIDDEN_PRODUCT);
    }
    if (Array.isArray(product.groupAlternatives) && product.groupAlternatives.some((item) => toNumber(item?.price) > 0)) {
      // Groepsprijzen overrulen de basisprijs in de webshop; die passen we hier bewust niet aan.
      warnings.push(PRICE_IMPORT_WARNING.GROUP_PRICES);
    }
    if (line.fileName && normalizeName(line.fileName) !== normalizeName(product.name)) {
      warnings.push(PRICE_IMPORT_WARNING.NAME_MISMATCH);
    }

    if (pricesAreEqual(oldPrice, parsedPrice.value)) {
      line.status = PRICE_IMPORT_STATUS.UNCHANGED;
      line.delta = 0;
      line.deltaPercentage = 0;
      return line;
    }

    line.status = PRICE_IMPORT_STATUS.CHANGE;
    line.delta = roundPrice(parsedPrice.value - oldPrice);
    line.deltaPercentage = oldPrice > 0 ? roundPrice(((parsedPrice.value - oldPrice) / oldPrice) * 100) : null;

    if (line.deltaPercentage !== null && line.deltaPercentage >= LARGE_CHANGE_PERCENTAGE) {
      warnings.push(PRICE_IMPORT_WARNING.LARGE_INCREASE);
    }
    if (line.deltaPercentage !== null && line.deltaPercentage <= -LARGE_CHANGE_PERCENTAGE) {
      warnings.push(PRICE_IMPORT_WARNING.LARGE_DECREASE);
    }

    return line;
  });
};

/**
 * Samenvatting voor de kop van het controlescherm. Bewust alleen de aantallen die daar ook echt
 * getoond worden; gemiddelden en uitersten bleken in de praktijk geen beslissing te ondersteunen.
 */
export const summarizePriceImportPlan = (lines, products) => {
  const changes = lines.filter((line) => line.status === PRICE_IMPORT_STATUS.CHANGE);
  const unchanged = lines.filter((line) => line.status === PRICE_IMPORT_STATUS.UNCHANGED);
  const errors = lines.filter((line) => line.status === PRICE_IMPORT_STATUS.ERROR);
  const warnings = lines.filter((line) => line.status !== PRICE_IMPORT_STATUS.ERROR && line.warnings.length > 0);

  const matchedProductIds = new Set(lines.map((line) => line.productId).filter(Boolean));
  const notInFileCount = (products || []).filter((product) => !matchedProductIds.has(product.id)).length;

  return {
    totalRows: lines.length,
    changeCount: changes.length,
    unchangedCount: unchanged.length,
    errorCount: errors.length,
    warningCount: warnings.length,
    notInFileCount,
  };
};

/** Regels die daadwerkelijk doorgevoerd kunnen worden. */
export const getApplicableLines = (lines) => lines.filter((line) => line.status === PRICE_IMPORT_STATUS.CHANGE);
